/**
 * Net-available time evaluation for client-facing booking.
 * Provider calendar create/edit must NOT use these guards (see availability PRD).
 */
import type { ProviderWeeklyAvailability } from './providerAvailabilityTypes';
import { PROVIDER_AVAILABILITY_UI_DAYS } from './providerAvailabilityTypes';

export interface MinuteRange {
  startMinute: number;
  endMinute: number;
}

function parseTimeLabel12h(label: string): { hour: number; minute: number } | null {
  const match = label.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return null;
  let hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  const period = match[3].toUpperCase();
  if (period === 'PM' && hour !== 12) hour += 12;
  if (period === 'AM' && hour === 12) hour = 0;
  return { hour, minute };
}

/** Unavailable minute ranges within an hour cell (0–60) from weekly template only. */
export function getUnavailableRangesInHourFromWeekly(
  day: Date,
  hour: number,
  weekly: ProviderWeeklyAvailability,
): MinuteRange[] {
  const dayKey = PROVIDER_AVAILABILITY_UI_DAYS[day.getDay()];
  const dayData = weekly[dayKey];
  const cellStartM = hour * 60;
  const cellEndM = (hour + 1) * 60;
  if (!dayData?.enabled || !dayData.slots?.length) {
    return [{ startMinute: 0, endMinute: 60 }];
  }
  const availableRel: MinuteRange[] = [];
  for (const slot of dayData.slots) {
    const startP = parseTimeLabel12h(slot.start);
    const endP = parseTimeLabel12h(slot.end);
    if (!startP || !endP) continue;
    const startM = startP.hour * 60 + startP.minute;
    const endM = endP.hour * 60 + endP.minute;
    const overlapStart = Math.max(startM, cellStartM);
    const overlapEnd = Math.min(endM, cellEndM);
    if (overlapStart < overlapEnd) {
      availableRel.push({
        startMinute: overlapStart - cellStartM,
        endMinute: overlapEnd - cellStartM,
      });
    }
  }
  if (availableRel.length === 0) return [{ startMinute: 0, endMinute: 60 }];
  availableRel.sort((a, b) => a.startMinute - b.startMinute);
  const merged: MinuteRange[] = [];
  for (const r of availableRel) {
    if (merged.length > 0 && r.startMinute <= merged[merged.length - 1].endMinute) {
      merged[merged.length - 1].endMinute = Math.max(merged[merged.length - 1].endMinute, r.endMinute);
    } else {
      merged.push({ ...r });
    }
  }
  const unavailable: MinuteRange[] = [];
  let pos = 0;
  for (const r of merged) {
    if (r.startMinute > pos) unavailable.push({ startMinute: pos, endMinute: r.startMinute });
    pos = Math.max(pos, r.endMinute);
  }
  if (pos < 60) unavailable.push({ startMinute: pos, endMinute: 60 });
  return unavailable;
}

/** True when a client-facing booking at `instant` falls inside net weekly availability (slots not subtracted yet). */
export function isInstantInWeeklyAvailability(
  instant: Date,
  weekly: ProviderWeeklyAvailability,
): boolean {
  const dayKey = PROVIDER_AVAILABILITY_UI_DAYS[instant.getDay()];
  const dayData = weekly[dayKey];
  if (!dayData?.enabled || !dayData.slots?.length) return false;
  const minuteOfDay = instant.getHours() * 60 + instant.getMinutes();
  for (const slot of dayData.slots) {
    const startP = parseTimeLabel12h(slot.start);
    const endP = parseTimeLabel12h(slot.end);
    if (!startP || !endP) continue;
    const startM = startP.hour * 60 + startP.minute;
    const endM = endP.hour * 60 + endP.minute;
    if (minuteOfDay >= startM && minuteOfDay < endM) return true;
  }
  return false;
}
